import { useEffect, useMemo, useState } from 'react';

import TableComponent from 'components/partials/table-component/table-component';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import {
  ViewWrapper,
  Chip,
} from 'components/partials/insight-modal/shared/components';
import { INSIGHT_SOURCES } from 'components/partials/insight-modal/shared/constants';
import type { InsightModalMetadata } from 'components/partials/insight-modal/shared/constants';
import { buildReportRelatedGoalsTableSettings } from 'components/partials/insight-modal/report/relative-goals-table-settings';
import { useReportGoals } from 'components/partials/insight-modal/report/hooks';
import type { GoalRecord } from 'components/partials/insight-modal/report/hooks';
import { METADATA_LABELS } from 'components/partials/insight-modal/report/constants';

import { useInsight } from 'components/partials/insight-modal/insight-context';

import { getFilterDisplayName } from 'constants/report-filters-mapping';

interface ReportMetadataFormProps {
  initialMetadata?: InsightModalMetadata;
}

const ReportMetadataForm = ({ initialMetadata }: ReportMetadataFormProps) => {
  const { state: insightState, dispatch } = useInsight();

  const reportActivePage = initialMetadata?.reportActivePage || null;
  const reportFilters = Array.isArray(initialMetadata?.reportFilters)
    ? initialMetadata.reportFilters
    : [];

  const { loadingGoals, allGoals } = useReportGoals(initialMetadata);

  const [linkedGoalIds, setLinkedGoalIds] = useState<(string | number)[]>(
    () => {
      if (insightState.relatedGoalIds.length > 0) {
        return insightState.relatedGoalIds.filter(
          (id): id is string | number =>
            typeof id === 'string' || typeof id === 'number'
        );
      }

      const raw = initialMetadata?.relatedGoalIds;

      return Array.isArray(raw)
        ? raw.filter(
            (id): id is string | number =>
              typeof id === 'string' || typeof id === 'number'
          )
        : [];
    }
  );

  const handleToggleChange = (goal: GoalRecord, isOn: boolean) => {
    const goalId = goal.id;

    if (goalId === undefined) {
      return;
    }

    setLinkedGoalIds((prev) => {
      const exists = prev.includes(goalId);

      let updatedIds: (string | number)[];

      if (isOn && !exists) {
        updatedIds = [...prev, goalId];
      } else if (!isOn && exists) {
        updatedIds = prev.filter((id) => id !== goalId);
      } else {
        updatedIds = prev;
      }

      dispatch({ type: 'setRelatedGoalIds', payload: updatedIds });

      return updatedIds;
    });
  };

  const columns = useMemo(() => {
    const metaClientId = initialMetadata?.clientId;
    const clientId = metaClientId;

    return buildReportRelatedGoalsTableSettings(
      linkedGoalIds,
      handleToggleChange,
      false,
      clientId
    );
  }, [linkedGoalIds, initialMetadata?.clientId]);

  useEffect(() => {
    const relatedGoals = allGoals
      .filter(
        (goal) => goal.id !== undefined && linkedGoalIds.includes(goal.id)
      )
      .map((goal) => goal.summary);

    dispatch({
      type: 'setMetadata',
      payload: {
        title: initialMetadata?.title,
        description: initialMetadata?.description,
        isReport: initialMetadata?.isReport,
        clientId: initialMetadata?.clientId,
        reportActivePage,
        reportFilters,
        reportId: initialMetadata?.reportId,
        relatedGoals,
        source: INSIGHT_SOURCES.REPORT,
        name: initialMetadata?.name,
      },
    });
  }, [
    dispatch,
    reportActivePage,
    reportFilters,
    linkedGoalIds,
    allGoals,
    initialMetadata?.title,
    initialMetadata?.description,
    initialMetadata?.name,
    initialMetadata?.reportId,
    initialMetadata?.isReport,
    initialMetadata?.clientId,
  ]);

  return (
    <ViewWrapper>
      <div className="text-navy-800 font-bold text-xl">
        {METADATA_LABELS.header}
      </div>
      <div>
        <div className="text-gray-700 font-semibold mb-1">
          {METADATA_LABELS.reportName}
        </div>
        <div className="flex flex-wrap">
          <Chip>{initialMetadata?.name || '—'}</Chip>
        </div>
      </div>
      <div className="flex items-start gap-5">
        <div>
          <div className="text-gray-700 mb-1 font-semibold">
            {METADATA_LABELS.filtersApplied}
          </div>
          <div className="flex flex-wrap gap-2">
            {reportFilters.length === 0 && <Chip>—</Chip>}
            {reportFilters.map((f, idx) => (
              <Chip key={`rf-${idx}`}>
                {getFilterDisplayName(f.column)}:{' '}
                {Array.isArray(f.values) ? f.values.join(', ') : '—'}
              </Chip>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="text-gray-700 mb-1 font-semibold">
          {METADATA_LABELS.relatedGoals}
        </div>
        <div className="relative">
          {!allGoals?.length && !loadingGoals ? (
            <EmptySpaceContent
              text={METADATA_LABELS.emptyGoalsText}
              rotation="horizontal"
              customClass="!h-[200px]"
            />
          ) : (
            <TableComponent
              columns={columns}
              data={allGoals}
              isLoading={loadingGoals}
              customHeight="max-h-[50vh]"
              pagination={false}
              showingRecordsTextIsOn={false}
              viewByAmountSelectIsOn={false}
            />
          )}
        </div>
      </div>
    </ViewWrapper>
  );
};

export default ReportMetadataForm;
